<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      phoneNumber: '',
      verificationCode: '',
      password2FA: '',
      // wizard steps: 'create' | 'verify_code' | 'verify_2fa'
      step: 'create',
      inboxId: null,
      isRequestingCode: false,
      isSubmittingCode: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations() {
    if (this.step === 'create') {
      return {
        channelName: { required },
        phoneNumber: { required },
      };
    }
    if (this.step === 'verify_code') {
      return {
        verificationCode: { required },
      };
    }
    if (this.step === 'verify_2fa') {
      return {
        password2FA: { required },
      };
    }
    return {};
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const eitaaChannel = await this.$store.dispatch('inboxes/createChannel', {
          name: this.channelName?.trim(),
          channel: {
            type: 'eitaa',
            phone_number: this.phoneNumber?.trim(),
          },
        });

        this.inboxId = eitaaChannel.id;
        await this.requestEitaaCode();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('INBOX_MGMT.ADD.EITAA_CHANNEL.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },

    async requestEitaaCode() {
      this.isRequestingCode = true;
      try {
        const res = await this.$store.dispatch('inboxes/eitaaSendCode', this.inboxId);
        if (res && (res.status === 'code_sent' || res.status === 'success' || res.status === 'already_authorized')) {
          if (res.status === 'success' || res.status === 'already_authorized') {
            useAlert(this.$t('INBOX_MGMT.ADD.EITAA_CHANNEL.ALREADY_CONNECTED'));
            this.proceedToAgents();
          } else {
            useAlert(this.$t('INBOX_MGMT.ADD.EITAA_CHANNEL.CODE_SENT_SUCCESS'));
            this.step = 'verify_code';
          }
        } else {
          throw new Error(res?.detail || 'Failed to request code');
        }
      } catch (error) {
        const errorMessage = error.message || 'Could not request code from Eitaa connector';
        useAlert(errorMessage);
      } finally {
        this.isRequestingCode = false;
      }
    },

    async submitVerificationCode() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isSubmittingCode = true;
      try {
        const res = await this.$store.dispatch('inboxes/eitaaVerify', {
          inboxId: this.inboxId,
          code: this.verificationCode?.trim(),
        });

        if (res && res.status === 'success') {
          useAlert(this.$t('INBOX_MGMT.ADD.EITAA_CHANNEL.AUTH_SUCCESS'));
          this.proceedToAgents();
        } else if (res && res.status === 'requires_2fa') {
          useAlert(this.$t('INBOX_MGMT.ADD.EITAA_CHANNEL.REQUIRES_2FA'));
          this.step = 'verify_2fa';
        } else {
          throw new Error(res?.detail || 'Invalid code');
        }
      } catch (error) {
        const errorMessage = error.message || 'Verification code submission failed';
        useAlert(errorMessage);
      } finally {
        this.isSubmittingCode = false;
      }
    },

    async submit2FAPassword() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isSubmittingCode = true;
      try {
        const res = await this.$store.dispatch('inboxes/eitaaVerify2FA', {
          inboxId: this.inboxId,
          password: this.password2FA?.trim(),
        });

        if (res && res.status === 'success') {
          useAlert(this.$t('INBOX_MGMT.ADD.EITAA_CHANNEL.AUTH_SUCCESS'));
          this.proceedToAgents();
        } else {
          throw new Error(res?.detail || 'Invalid 2FA password');
        }
      } catch (error) {
        const errorMessage = error.message || '2FA verification failed';
        useAlert(errorMessage);
      } finally {
        this.isSubmittingCode = false;
      }
    },

    proceedToAgents() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: this.inboxId,
        },
      });
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.DESC')"
    />

    <!-- STEP 1: CREATE CHANNEL -->
    <form
      v-if="step === 'create'"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName?.$error }">
          {{ $t('INBOX_MGMT.ADD.EITAA_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.CHANNEL_NAME.PLACEHOLDER')"
            @blur="v$.channelName?.$touch"
          />
          <span v-if="v$.channelName?.$error" class="message">{{
            $t('INBOX_MGMT.ADD.EITAA_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.phoneNumber?.$error }">
          {{ $t('INBOX_MGMT.ADD.EITAA_CHANNEL.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.PHONE_NUMBER.PLACEHOLDER')"
            @blur="v$.phoneNumber?.$touch"
          />
          <span v-if="v$.phoneNumber?.$error" class="message">{{
            $t('INBOX_MGMT.ADD.EITAA_CHANNEL.PHONE_NUMBER.ERROR')
          }}</span>
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating || isRequestingCode"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>

    <!-- STEP 2: VERIFY CODE -->
    <form
      v-if="step === 'verify_code'"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="submitVerificationCode()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.verificationCode?.$error }">
          {{ $t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.LABEL') }}
          <input
            v-model="verificationCode"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.PLACEHOLDER')"
            @blur="v$.verificationCode?.$touch"
          />
          <span v-if="v$.verificationCode?.$error" class="message">{{
            $t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.ERROR')
          }}</span>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4 flex gap-4">
        <NextButton
          :is-loading="isSubmittingCode"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.SUBMIT')"
        />
        <NextButton
          type="button"
          :is-loading="isRequestingCode"
          @click="requestEitaaCode"
          :label="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.RESEND')"
        />
      </div>
    </form>

    <!-- STEP 3: VERIFY 2FA -->
    <form
      v-if="step === 'verify_2fa'"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="submit2FAPassword()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.password2FA?.$error }">
          {{ $t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.LABEL') }}
          <input
            v-model="password2FA"
            type="password"
            :placeholder="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.PLACEHOLDER')"
            @blur="v$.password2FA?.$touch"
          />
          <span v-if="v$.password2FA?.$error" class="message">{{
            $t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.ERROR')
          }}</span>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isSubmittingCode"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.SUBMIT')"
        />
      </div>
    </form>
  </div>
</template>
